import { defineComponent, PropType } from 'vue';
import styles from './index.module.less';
import { dayjs } from 'element-plus';
import clsx from 'clsx';

interface FaultSource {
  frameId: string;
  moduleName: string;
  errorCode?: number;
  errorCodeString: string;
}

interface FaultSourceField {
  label: string;
  value: string;
}

interface AbnormalInfo {
  ip: string;
  errorCode: string;
  errorLevel: string;
  errorCodeString: string;
  errorMsg: string;
  timestamp: number;
  moduleName?: string;
  faultSource?: FaultSource[];
}

const props = {
  errList: {
    type: Array as PropType<AbnormalInfo[]>,
    default: [],
  },
  showDetail: {
    type: Boolean,
    default: false,
  },
};

/**
 * 后端返回的 JSON 可能是小驼峰（moduleName），
 * 也可能是 proto 原始下划线（module_name），这里统一取值。
 */
function pickString(obj: any, camelKey: string, snakeKey: string): string {
  return obj?.[camelKey] ?? obj?.[snakeKey] ?? '-';
}

/**
 * 故障源中需要展示的字段（errorCode 不展示），只展示非空值
 */
const FAULT_SOURCE_FIELDS: Array<{
  label: string;
  camelKey: string;
  snakeKey: string;
}> = [
  { label: 'FrameId', camelKey: 'frameId', snakeKey: 'frame_id' },
  { label: 'Module', camelKey: 'moduleName', snakeKey: 'module_name' },
  {
    label: 'ErrorCode',
    camelKey: 'errorCodeString',
    snakeKey: 'error_code_string',
  },
];

/**
 * 兼容 proto 定义的 repeated FaultSource fault_source = 9：
 * 后端返回的 JSON 可能是小驼峰（faultSource/frameId），
 * 也可能是原始下划线（fault_source/frame_id），这里统一取值。
 * 每个故障源只保留有值的字段，全部为空的故障源直接过滤掉。
 */
function normalizeFaultSource(item: AbnormalInfo): FaultSourceField[][] {
  const raw = (item as any)?.faultSource ?? (item as any)?.fault_source;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map((source: any) =>
      FAULT_SOURCE_FIELDS.reduce<FaultSourceField[]>(
        (fields, { label, camelKey, snakeKey }) => {
          const value = String(
            source?.[camelKey] || source?.[snakeKey] || ''
          ).trim();
          if (value) {
            fields.push({ label, value });
          }
          return fields;
        },
        []
      )
    )
    .filter((fields) => fields.length > 0);
}

const VarguardItem = defineComponent({
  name: 'VarguardItem',
  props,
  setup(props) {
    return {
      props,
    };
  },
  render() {
    return (
      <div>
        {this.props.errList.map((item, index) => {
          const faultSourceList = normalizeFaultSource(item);
          return (
            <div
              class={clsx(styles['error-item'], {
                [styles['show-detail']]: this.props.showDetail,
              })}
              key={index}
            >
              <div class={styles.row}>
                <span class={styles.detail}>
                  时间：{dayjs(item.timestamp / 1000000).format('HH:mm:ss')}
                </span>
                <span class={styles.detail}>IP：{item.ip}</span>
              </div>
              <div class={styles.row}>
                <span class={styles.detail}>
                  模块：{pickString(item, 'moduleName', 'module_name')}
                </span>
              </div>
              <div class={styles.row}>
                <span class={styles.detail}>异常编码：{item.errorCode}</span>
                <span class={styles.detail}>异常级别：{item.errorLevel}</span>
              </div>
              <div class={styles.row}>
                <span class={styles.detail}>
                  ErrorCode：{item.errorCodeString}
                </span>
              </div>
              <div class={styles.row}>
                <span class={styles.detail}>Message：{item.errorMsg}</span>
              </div>
              {!!faultSourceList.length && (
                <div class={styles['fault-source']}>
                  <div class={styles['fault-source-title']}>
                    故障源（{faultSourceList.length}）：
                  </div>
                  {faultSourceList.map((fields, sourceIndex) => (
                    <div class={styles['fault-source-item']} key={sourceIndex}>
                      {fields.map((field) => (
                        <div class={styles.row} key={field.label}>
                          <span class={styles.detail}>
                            {field.label}：{field.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  },
});

export default VarguardItem;
